import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";

import api from "../api/api";

import {
    Alert,
    Button,
    Card,
    Col,
    Empty,
    Input,
    Row,
    Select,
    Space,
    Spin,
    Statistic,
    Tag,
    Typography,
    message,
} from "antd";

import {
    ApartmentOutlined,
    BankOutlined,
    BookOutlined,
    CalendarOutlined,
    ClearOutlined,
    FilterOutlined,
    FolderOpenOutlined,
    ReadOutlined,
    ReloadOutlined,
    RightOutlined,
    SearchOutlined,
    TeamOutlined,
} from "@ant-design/icons";

import { useAcademicYear } from "../AcademicYearContext";

import CurriculumTeacherWorkspacePopup from "./CurriculumTeacherWorkspacePopup";

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

const SEARCH_DEBOUNCE = 350;

const COLORS = {
    navy: "#1B2A41",
    khaki: "#C3B091",
    green: "#16a34a",
    orange: "#f97316",
    blue: "#2563eb",
    red: "#dc2626",
    gray: "#64748b",
    light: "#f8fafc",
    border: "#e2e8f0",
    white: "#ffffff",
};

// ============================================================
// GENERAL HELPERS
// ============================================================

const normalizeArrayResponse = (data) => {
    if (Array.isArray(data)) {
        return data;
    }

    if (Array.isArray(data?.results)) {
        return data.results;
    }

    if (Array.isArray(data?.data)) {
        return data.data;
    }

    if (Array.isArray(data?.topics)) {
        return data.topics;
    }

    return [];
};

const normalizeText = (value) => {
    if (
        value === null ||
        value === undefined
    ) {
        return "";
    }

    if (typeof value === "string") {
        return value.trim();
    }

    if (Array.isArray(value)) {
        return value
            .map((item) =>
                normalizeText(item)
            )
            .filter(Boolean)
            .join(", ");
    }

    if (typeof value === "object") {
        return (
            value.name ||
            value.label ||
            value.title ||
            value.display_name ||
            value.semester_display ||
            ""
        )
            .toString()
            .trim();
    }

    return String(value).trim();
};

const getId = (value) => {
    if (
        value === null ||
        value === undefined
    ) {
        return null;
    }

    if (
        typeof value === "number" ||
        typeof value === "string"
    ) {
        return value;
    }

    if (typeof value === "object") {
        return (
            value.id ??
            value.value ??
            value.pk ??
            null
        );
    }

    return null;
};

const getName = (value) => {
    if (
        value === null ||
        value === undefined
    ) {
        return "";
    }

    if (typeof value === "string") {
        return value.trim();
    }

    if (typeof value === "object") {
        return normalizeText(
            value.name ??
                value.label ??
                value.title ??
                value.display_name
        );
    }

    return String(value).trim();
};

// ============================================================
// SEMESTER NORMALIZATION
// ============================================================

const SEMESTER_ORDER = {
    semester_one: 1,
    semester_two: 2,
    semester_three: 3,
    semester_four: 4,

    semester_1: 1,
    semester_2: 2,
    semester_3: 3,
    semester_4: 4,

    "semester 1": 1,
    "semester 2": 2,
    "semester 3": 3,
    "semester 4": 4,

    "semester one": 1,
    "semester two": 2,
    "semester three": 3,
    "semester four": 4,

    "1": 1,
    "2": 2,
    "3": 3,
    "4": 4,
};

const normalizeSemesterKey = (value) => {
    return normalizeText(value)
        .toLowerCase()
        .replace(/-/g, "_")
        .replace(/\s+/g, " ")
        .trim();
};

const getSemesterOrder = (value) => {
    const key =
        normalizeSemesterKey(value);

    return (
        SEMESTER_ORDER[key] ??
        999
    );
};

const formatSemesterFromValue = (
    value
) => {
    if (
        value === null ||
        value === undefined
    ) {
        return "";
    }

    const key =
        normalizeSemesterKey(value);

    switch (key) {
        case "semester_one":
        case "semester_1":
        case "semester 1":
        case "1":
        case "semester one":
            return "Semester One";

        case "semester_two":
        case "semester_2":
        case "semester 2":
        case "2":
        case "semester two":
            return "Semester Two";

        case "semester_three":
        case "semester_3":
        case "semester 3":
        case "3":
        case "semester three":
            return "Semester Three";

        case "semester_four":
        case "semester_4":
        case "semester 4":
        case "4":
        case "semester four":
            return "Semester Four";

        default:
            return normalizeText(value);
    }
};

// ============================================================
// PERIOD NORMALIZATION
// ============================================================

const ROMAN_PERIODS = {
    I: 1,
    II: 2,
    III: 3,
    IV: 4,
    V: 5,
    VI: 6,
    VII: 7,
    VIII: 8,
    IX: 9,
    X: 10,
};

const getRomanPeriod = (number) => {
    const numericValue = Number(number);

    if (
        !Number.isFinite(
            numericValue
        )
    ) {
        return "";
    }

    return (
        Object.entries(
            ROMAN_PERIODS
        ).find(
            ([, value]) =>
                value === numericValue
        )?.[0] || ""
    );
};

const getPeriodNumber = (topic) => {
    /*
     * IMPORTANT:
     *
     * period_number / period_order represent the
     * actual curriculum period.
     *
     * period_id is only a database identifier and
     * must NOT be interpreted as Period 1, 2, 3, etc.
     */

    const candidates = [
        topic?.period_number,
        topic?.period_order,

        topic?.period?.period_number,
        topic?.period?.order,

        topic?.period?.number,
    ];

    for (
        const value of candidates
    ) {
        if (
            value === null ||
            value === undefined ||
            value === ""
        ) {
            continue;
        }

        if (
            typeof value === "number" &&
            Number.isFinite(value)
        ) {
            return value;
        }

        if (
            typeof value === "string"
        ) {
            const text =
                value.trim();

            if (!text) {
                continue;
            }

            if (
                /^\d+$/.test(text)
            ) {
                const number =
                    Number(text);

                if (
                    Number.isFinite(
                        number
                    )
                ) {
                    return number;
                }
            }

            const roman =
                text
                    .toUpperCase()
                    .replace(
                        /^PERIOD\s+/i,
                        ""
                    )
                    .trim();

            if (
                Object.prototype.hasOwnProperty.call(
                    ROMAN_PERIODS,
                    roman
                )
            ) {
                return ROMAN_PERIODS[
                    roman
                ];
            }

            const match =
                text.match(
                    /(?:period\s*)?(\d+)/i
                );

            if (match) {
                const number =
                    Number(
                        match[1]
                    );

                if (
                    Number.isFinite(
                        number
                    )
                ) {
                    return number;
                }
            }
        }

        if (
            typeof value === "object"
        ) {
            const nestedNumber =
                value?.period_number ??
                value?.order ??
                value?.number ??
                null;

            if (
                nestedNumber !== null &&
                nestedNumber !== undefined
            ) {
                const number =
                    Number(
                        nestedNumber
                    );

                if (
                    Number.isFinite(
                        number
                    )
                ) {
                    return number;
                }
            }
        }
    }

    return null;
};

// ============================================================
// SUBJECT HELPERS
// ============================================================

const getSubjectId = (topic) => {
    /*
     * The backend serializer returns flat subject fields.
     * Nested period data remains a compatibility fallback.
     */

    return (
        topic?.subject_id ??
        topic?.period?.subject_id ??
        getId(
            topic?.subject
        ) ??
        getId(
            topic?.period?.subject
        ) ??
        null
    );
};

const getSubjectName = (topic) => {
    return (
        normalizeText(
            topic?.subject_name
        ) ||
        normalizeText(
            topic?.period?.subject_name
        ) ||
        getName(
            topic?.subject
        ) ||
        getName(
            topic?.period?.subject
        ) ||
        "Subject"
    );
};

// ============================================================
// GRADE HELPERS
// ============================================================

const getGradeId = (topic) => {
    return (
        topic?.grade_class_id ??
        topic?.period?.grade_class_id ??
        getId(
            topic?.grade_class
        ) ??
        getId(
            topic?.period?.grade_class
        ) ??
        null
    );
};

const getGradeName = (topic) => {
    return (
        normalizeText(
            topic?.grade_name
        ) ||
        normalizeText(
            topic?.grade_class_name
        ) ||
        normalizeText(
            topic?.period?.grade_name
        ) ||
        normalizeText(
            topic?.period?.grade_class_name
        ) ||
        getName(
            topic?.grade_class
        ) ||
        getName(
            topic?.period?.grade_class
        ) ||
        "Grade / Class"
    );
};

// ============================================================
// SEMESTER HELPERS
// ============================================================

const getSemesterValue = (topic) => {
    /*
     * IMPORTANT:
     *
     * The backend CurriculumTopicSerializer exposes:
     *
     * semester
     * semester_display
     *
     * as FLAT fields.
     *
     * Therefore the flat value is authoritative.
     *
     * We only use topic.period.semester as a fallback
     * for older API responses.
     */

    const flatSemester =
        topic?.semester ??
        topic?.semester_value ??
        topic?.semester_code ??
        null;

    if (
        flatSemester !== null &&
        flatSemester !== undefined &&
        normalizeText(
            flatSemester
        )
    ) {
        return flatSemester;
    }

    return (
        topic?.period?.semester ??
        topic?.period?.semester_value ??
        topic?.period?.semester_code ??
        null
    );
};

const getSemesterName = (topic) => {
    /*
     * Prefer the backend's explicit display value.
     */

    const explicitDisplay =
        normalizeText(
            topic?.semester_display
        ) ||
        normalizeText(
            topic?.semester_name
        );

    if (explicitDisplay) {
        return explicitDisplay;
    }

    const semesterValue =
        getSemesterValue(
            topic
        );

    const formatted =
        formatSemesterFromValue(
            semesterValue
        );

    if (formatted) {
        return formatted;
    }

    /*
     * Compatibility fallback for nested API responses.
     */

    return (
        normalizeText(
            topic?.period
                ?.semester_display
        ) ||
        normalizeText(
            topic?.period
                ?.semester_name
        ) ||
        "Semester"
    );
};

// ============================================================
// PERIOD HELPERS
// ============================================================

const getPeriodId = (topic) => {
    /*
     * Database ID.
     *
     * This is useful for identifying a record but MUST
     * NOT be used to determine the visible period number.
     */

    return (
        topic?.period_id ??
        topic?.period?.id ??
        null
    );
};

const getPeriodName = (topic) => {
    /*
     * Use the backend's explicit period name first.
     */

    const explicitName =
        normalizeText(
            topic?.period_name
        ) ||
        normalizeText(
            topic?.period?.name
        ) ||
        normalizeText(
            topic?.period?.period_name
        );

    if (explicitName) {
        return explicitName;
    }

    /*
     * Otherwise derive it from the actual period number.
     */

    const periodNumber =
        getPeriodNumber(
            topic
        );

    if (
        periodNumber !== null &&
        periodNumber !== undefined
    ) {
        const roman =
            getRomanPeriod(
                periodNumber
            );

        if (roman) {
            return `Period ${roman}`;
        }

        return `Period ${periodNumber}`;
    }

    return "Period";
};

// ============================================================
// CURRICULUM HELPERS
// ============================================================

const getCurriculumId = (topic) => {
    return (
        topic?.curriculum_id ??
        topic?.period?.curriculum_id ??
        getId(
            topic?.curriculum
        ) ??
        getId(
            topic?.period?.curriculum
        ) ??
        null
    );
};

const getCurriculumName = (topic) => {
    return (
        normalizeText(
            topic?.curriculum_name
        ) ||
        normalizeText(
            topic?.period?.curriculum_name
        ) ||
        getName(
            topic?.curriculum
        ) ||
        getName(
            topic?.period?.curriculum
        ) ||
        "Curriculum"
    );
};

// ============================================================
// TOPIC PREVIEW
// ============================================================

const getPreviewText = (topic) => {
    const candidates = [
        topic?.content,
        topic?.learning_objectives,
        topic?.learning_outcomes,
        topic?.competencies,
        topic?.suggested_activities,
        topic?.assessment_guidance,
        topic?.teaching_resources,
        topic?.references,
    ];

    for (
        const value of candidates
    ) {
        const text =
            normalizeText(
                value
            );

        if (text) {
            return text;
        }
    }

    return "No curriculum description was provided for this topic.";
};

// ============================================================
// SEARCH TEXT
// ============================================================

const getTopicSearchText = (
    topic
) => {
    return [
        topic?.name,
        topic?.content,
        topic?.learning_objectives,
        topic?.learning_outcomes,
        topic?.competencies,
        topic?.suggested_activities,
        topic?.assessment_guidance,
        topic?.teaching_resources,
        topic?.references,

        getSubjectName(topic),
        getGradeName(topic),
        getSemesterName(topic),
        getPeriodName(topic),
        getCurriculumName(topic),
    ]
        .map(normalizeText)
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
};

// ============================================================
// SEMESTER OPTIONS
// ============================================================

const normalizeSemesterOptions = (
    data
) => {
    const map = new Map();

    const rawSemesters =
        Array.isArray(
            data?.semesters
        )
            ? data.semesters
            : [];

    rawSemesters.forEach(
        (semester) => {
            const value =
                semester?.value ??
                semester?.semester ??
                semester?.id ??
                null;

            if (
                value === null ||
                value === undefined
            ) {
                return;
            }

            const name =
                normalizeText(
                    semester?.name
                ) ||
                normalizeText(
                    semester?.semester_display
                ) ||
                normalizeText(
                    semester?.semester_name
                ) ||
                formatSemesterFromValue(
                    value
                );

            if (!name) {
                return;
            }

            map.set(
                String(value),
                {
                    value,
                    name,
                }
            );
        }
    );

    if (
        data?.semester_topic_counts &&
        typeof data.semester_topic_counts ===
            "object"
    ) {
        Object.entries(
            data.semester_topic_counts
        ).forEach(
            ([
                value,
                countData,
            ]) => {
                const name =
                    normalizeText(
                        countData?.name
                    ) ||
                    normalizeText(
                        countData?.semester_display
                    ) ||
                    formatSemesterFromValue(
                        value
                    );

                if (!name) {
                    return;
                }

                map.set(
                    String(value),
                    {
                        value,
                        name,
                    }
                );
            }
        );
    }

    /*
     * Last-resort fallback:
     *
     * If the backend gives no semester metadata,
     * derive the list from the topics.
     */

    if (map.size === 0) {
        const results =
            normalizeArrayResponse(
                data
            );

        results.forEach(
            (topic) => {
                const value =
                    getSemesterValue(
                        topic
                    );

                if (
                    value === null ||
                    value === undefined
                ) {
                    return;
                }

                const name =
                    getSemesterName(
                        topic
                    );

                if (!name) {
                    return;
                }

                map.set(
                    String(value),
                    {
                        value,
                        name,
                    }
                );
            }
        );
    }

    return Array.from(
        map.values()
    ).sort(
        (a, b) => {
            const aOrder =
                getSemesterOrder(
                    a.value
                );

            const bOrder =
                getSemesterOrder(
                    b.value
                );

            if (
                aOrder !==
                bOrder
            ) {
                return (
                    aOrder -
                    bOrder
                );
            }

            return a.name.localeCompare(
                b.name
            );
        }
    );
};

// ============================================================
// API ERROR
// ============================================================

const getApiErrorMessage = (
    error,
    fallback = "Something went wrong."
) => {
    if (!error) {
        return fallback;
    }

    const data =
        error?.response?.data;

    if (
        typeof data === "string" &&
        data.trim()
    ) {
        return data;
    }

    if (data?.detail) {
        return data.detail;
    }

    if (data?.message) {
        return data.message;
    }

    if (data?.error) {
        return data.error;
    }

    if (
        data &&
        typeof data === "object"
    ) {
        const values =
            Object.values(data)
                .flatMap(
                    (value) => {
                        if (
                            Array.isArray(
                                value
                            )
                        ) {
                            return value;
                        }

                        if (
                            typeof value ===
                            "string"
                        ) {
                            return [
                                value,
                            ];
                        }

                        return [];
                    }
                )
                .filter(Boolean);

        if (values.length) {
            return values.join(
                " "
            );
        }
    }

    if (error?.message) {
        return error.message;
    }

    return fallback;
};

// ============================================================
// MERGE TOPIC DATA
// ============================================================

const mergeTopicData = (
    baseTopic,
    detailedTopic
) => {
    const merged = {
        ...(baseTopic || {}),
        ...(detailedTopic || {}),
    };

    /*
     * Preserve nested period information.
     */
    if (
        baseTopic?.period ||
        detailedTopic?.period
    ) {
        merged.period = {
            ...(baseTopic?.period || {}),
            ...(detailedTopic?.period || {}),
        };
    }

    /*
     * Search response is already authoritative for
     * the curriculum structure.
     *
     * Preserve its flat semester/period information
     * when the detail endpoint does not provide it.
     */

    if (
        baseTopic?.semester !==
            null &&
        baseTopic?.semester !==
            undefined
    ) {
        merged.semester =
            detailedTopic?.semester ??
            baseTopic.semester;
    }

    if (
        baseTopic?.semester_display
    ) {
        merged.semester_display =
            detailedTopic?.semester_display ||
            baseTopic.semester_display;
    }

    if (
        baseTopic?.period_number !==
            null &&
        baseTopic?.period_number !==
            undefined
    ) {
        merged.period_number =
            detailedTopic?.period_number ??
            baseTopic.period_number;
    }

    if (
        baseTopic?.period_order !==
            null &&
        baseTopic?.period_order !==
            undefined
    ) {
        merged.period_order =
            detailedTopic?.period_order ??
            baseTopic.period_order;
    }

    if (
        baseTopic?.period_name
    ) {
        merged.period_name =
            detailedTopic?.period_name ||
            baseTopic.period_name;
    }

    if (
        baseTopic?.period_id !==
            null &&
        baseTopic?.period_id !==
            undefined
    ) {
        merged.period_id =
            detailedTopic?.period_id ??
            baseTopic.period_id;
    }

    return merged;
};

// ============================================================
// PERIOD GROUP KEY
// ============================================================

const getPeriodGroupKey = (
    topic
) => {
    const semesterValue =
        getSemesterValue(
            topic
        );

    const semesterKey =
        normalizeSemesterKey(
            semesterValue
        ) ||
        "unknown_semester";

    const periodNumber =
        getPeriodNumber(
            topic
        );

    if (
        periodNumber !== null &&
        periodNumber !== undefined
    ) {
        return `${semesterKey}:${periodNumber}`;
    }

    const periodId =
        getPeriodId(
            topic
        );

    if (
        periodId !== null &&
        periodId !== undefined
    ) {
        return `${semesterKey}:id:${periodId}`;
    }

    return `${semesterKey}:unknown`;
};

// ============================================================
// COMPONENT
// ============================================================

const CurriculumTeacherWorkspace =
    () => {
        const {
            selectedYear,
        } = useAcademicYear();

        // ====================================================
        // DATA
        // ====================================================

        const [
            topics,
            setTopics,
        ] = useState([]);

        const [
            availableSemesters,
            setAvailableSemesters,
        ] = useState([]);

        const [
            loading,
            setLoading,
        ] = useState(false);

        const [
            initialLoading,
            setInitialLoading,
        ] = useState(true);

        const [
            semestersLoading,
            setSemestersLoading,
        ] = useState(false);

        const [
            error,
            setError,
        ] = useState("");

        // ====================================================
        // SEARCH / FILTER
        // ====================================================

        const [
            searchText,
            setSearchText,
        ] = useState("");

        const [
            selectedSubject,
            setSelectedSubject,
        ] = useState(null);

        const [
            selectedGrade,
            setSelectedGrade,
        ] = useState(null);

        const [
            selectedSemester,
            setSelectedSemester,
        ] = useState(null);

        // ====================================================
        // POPUP
        // ====================================================

        const [
            topicModalOpen,
            setTopicModalOpen,
        ] = useState(false);

        const [
            selectedTopic,
            setSelectedTopic,
        ] = useState(null);

        const [
            loadingTopic,
            setLoadingTopic,
        ] = useState(false);

        // ====================================================
        // REQUEST REFS
        // ====================================================

        const requestSequenceRef =
            useRef(0);

        const semesterRequestSequenceRef =
            useRef(0);

        const topicRequestSequenceRef =
            useRef(0);

        // ====================================================
        // MESSAGE
        // ====================================================

        const [
            messageApi,
            contextHolder,
        ] = message.useMessage();

        // ====================================================
        // LOAD ALL AVAILABLE SEMESTERS
        // ====================================================

        const loadAvailableSemesters =
            useCallback(
                async () => {
                    const academicYearId =
                        selectedYear?.id;

                    if (
                        !academicYearId
                    ) {
                        setAvailableSemesters(
                            []
                        );

                        setSemestersLoading(
                            false
                        );

                        return;
                    }

                    const requestId =
                        ++semesterRequestSequenceRef.current;

                    setSemestersLoading(
                        true
                    );

                    try {
                        const response =
                            await api.get(
                                "/api/search/",
                                {
                                    params: {
                                        academic_year:
                                            academicYearId,
                                    },
                                }
                            );

                        if (
                            requestId !==
                            semesterRequestSequenceRef.current
                        ) {
                            return;
                        }

                        const responseData =
                            response?.data;

                        if (
                            responseData?.success ===
                            false
                        ) {
                            throw new Error(
                                responseData?.message ||
                                    "Unable to load available semesters."
                            );
                        }

                        const semesters =
                            normalizeSemesterOptions(
                                responseData
                            );

                        setAvailableSemesters(
                            semesters
                        );
                    } catch (
                        requestError
                    ) {
                        if (
                            requestId !==
                            semesterRequestSequenceRef.current
                        ) {
                            return;
                        }

                        messageApi.warning(
                            getApiErrorMessage(
                                requestError,
                                "Unable to load the available curriculum semesters."
                            )
                        );
                    } finally {
                        if (
                            requestId ===
                            semesterRequestSequenceRef.current
                        ) {
                            setSemestersLoading(
                                false
                            );
                        }
                    }
                },
                [
                    selectedYear?.id,
                    messageApi,
                ]
            );

        // ====================================================
        // LOAD SEMESTERS WHEN YEAR CHANGES
        // ====================================================

        useEffect(() => {
            ++semesterRequestSequenceRef.current;

            setSelectedSemester(
                null
            );

            setAvailableSemesters(
                []
            );

            if (
                !selectedYear?.id
            ) {
                setSemestersLoading(
                    false
                );

                return undefined;
            }

            loadAvailableSemesters();

            return undefined;
        }, [
            selectedYear?.id,
            loadAvailableSemesters,
        ]);

        // ====================================================
        // SEARCH CURRICULUM
        // ====================================================

        const searchCurriculum =
            useCallback(
                async (
                    query = ""
                ) => {
                    const academicYearId =
                        selectedYear?.id;

                    if (
                        !academicYearId
                    ) {
                        setTopics([]);

                        setError("");

                        setLoading(
                            false
                        );

                        setInitialLoading(
                            false
                        );

                        return;
                    }

                    const requestId =
                        ++requestSequenceRef.current;

                    setLoading(
                        true
                    );

                    setError("");

                    try {
                        const params =
                            {
                                academic_year:
                                    academicYearId,

                                q: normalizeText(
                                    query
                                ),
                            };

                        if (
                            selectedSubject
                        ) {
                            params.subject =
                                selectedSubject;
                        }

                        if (
                            selectedGrade
                        ) {
                            params.grade_class =
                                selectedGrade;
                        }

                        if (
                            selectedSemester
                        ) {
                            params.semester =
                                selectedSemester;
                        }

                        const response =
                            await api.get(
                                "/api/search/",
                                {
                                    params,
                                }
                            );

                        if (
                            requestId !==
                            requestSequenceRef.current
                        ) {
                            return;
                        }

                        const responseData =
                            response?.data;

                        if (
                            responseData?.success ===
                            false
                        ) {
                            throw new Error(
                                responseData?.message ||
                                    "Curriculum search failed."
                            );
                        }

                        const results =
                            normalizeArrayResponse(
                                responseData
                            );

                        /*
                         * Do not overwrite availableSemesters here.
                         *
                         * The current search may be filtered to
                         * Semester One or Semester Two.
                         */
                        setTopics(
                            results
                        );
                    } catch (
                        requestError
                    ) {
                        if (
                            requestId !==
                            requestSequenceRef.current
                        ) {
                            return;
                        }

                        const errorMessage =
                            getApiErrorMessage(
                                requestError,
                                "Unable to load curriculum topics."
                            );

                        setTopics([]);

                        setError(
                            errorMessage
                        );
                    } finally {
                        if (
                            requestId ===
                            requestSequenceRef.current
                        ) {
                            setLoading(
                                false
                            );

                            setInitialLoading(
                                false
                            );
                        }
                    }
                },
                [
                    selectedYear?.id,
                    selectedSubject,
                    selectedGrade,
                    selectedSemester,
                ]
            );

        // ====================================================
        // SEARCH EFFECT
        // ====================================================

        useEffect(() => {
            let timeoutId;

            if (
                !selectedYear?.id
            ) {
                setTopics([]);

                setInitialLoading(
                    false
                );

                return undefined;
            }

            timeoutId =
                setTimeout(() => {
                    searchCurriculum(
                        searchText
                    );
                }, SEARCH_DEBOUNCE);

            return () => {
                clearTimeout(
                    timeoutId
                );
            };
        }, [
            selectedYear?.id,
            selectedSubject,
            selectedGrade,
            selectedSemester,
            searchText,
            searchCurriculum,
        ]);

        // ====================================================
        // SUBJECT OPTIONS
        // ====================================================

        const subjectOptions =
            useMemo(() => {
                const map =
                    new Map();

                topics.forEach(
                    (topic) => {
                        const id =
                            getSubjectId(
                                topic
                            );

                        const name =
                            getSubjectName(
                                topic
                            );

                        if (
                            id !== null &&
                            id !== undefined &&
                            name
                        ) {
                            map.set(
                                String(id),
                                {
                                    id,
                                    name,
                                }
                            );
                        }
                    }
                );

                return Array.from(
                    map.values()
                ).sort(
                    (a, b) =>
                        a.name.localeCompare(
                            b.name
                        )
                );
            }, [topics]);

        // ====================================================
        // GRADE OPTIONS
        // ====================================================

        const gradeOptions =
            useMemo(() => {
                const map =
                    new Map();

                topics.forEach(
                    (topic) => {
                        const id =
                            getGradeId(
                                topic
                            );

                        const name =
                            getGradeName(
                                topic
                            );

                        if (
                            id !== null &&
                            id !== undefined &&
                            name
                        ) {
                            map.set(
                                String(id),
                                {
                                    id,
                                    name,
                                }
                            );
                        }
                    }
                );

                return Array.from(
                    map.values()
                ).sort(
                    (a, b) =>
                        a.name.localeCompare(
                            b.name
                        )
                );
            }, [topics]);

        // ====================================================
        // SEMESTER OPTIONS
        // ====================================================

        const semesterOptions =
            useMemo(() => {
                return [
                    ...availableSemesters,
                ].sort(
                    (a, b) => {
                        const aOrder =
                            getSemesterOrder(
                                a.value
                            );

                        const bOrder =
                            getSemesterOrder(
                                b.value
                            );

                        if (
                            aOrder !==
                            bOrder
                        ) {
                            return (
                                aOrder -
                                bOrder
                            );
                        }

                        return a.name.localeCompare(
                            b.name
                        );
                    }
                );
            }, [
                availableSemesters,
            ]);

        // ====================================================
        // PROTECT SELECTED SEMESTER
        // ====================================================

        useEffect(() => {
            if (
                semestersLoading
            ) {
                return;
            }

            if (
                !selectedSemester
            ) {
                return;
            }

            if (
                semesterOptions.length ===
                0
            ) {
                return;
            }

            const exists =
                semesterOptions.some(
                    (semester) =>
                        String(
                            semester.value
                        ) ===
                        String(
                            selectedSemester
                        )
                );

            if (!exists) {
                setSelectedSemester(
                    null
                );
            }
        }, [
            semesterOptions,
            selectedSemester,
            semestersLoading,
        ]);

        // ====================================================
        // CLIENT-SIDE SEARCH
        // ====================================================

        const visibleTopics =
            useMemo(() => {
                const query =
                    normalizeText(
                        searchText
                    ).toLowerCase();

                if (!query) {
                    return topics;
                }

                return topics.filter(
                    (topic) =>
                        getTopicSearchText(
                            topic
                        ).includes(
                            query
                        )
                );
            }, [
                topics,
                searchText,
            ]);

        // ====================================================
        // SORT TOPICS
        // ====================================================

        const sortedTopics =
            useMemo(() => {
                return [
                    ...visibleTopics,
                ].sort(
                    (a, b) => {
                        const semesterDifference =
                            getSemesterOrder(
                                getSemesterValue(
                                    a
                                )
                            ) -
                            getSemesterOrder(
                                getSemesterValue(
                                    b
                                )
                            );

                        if (
                            semesterDifference !==
                            0
                        ) {
                            return semesterDifference;
                        }

                        const periodA =
                            getPeriodNumber(
                                a
                            );

                        const periodB =
                            getPeriodNumber(
                                b
                            );

                        if (
                            periodA !== null &&
                            periodB !== null &&
                            periodA !==
                                periodB
                        ) {
                            return (
                                periodA -
                                periodB
                            );
                        }

                        if (
                            periodA !== null &&
                            periodB === null
                        ) {
                            return -1;
                        }

                        if (
                            periodA === null &&
                            periodB !== null
                        ) {
                            return 1;
                        }

                        return normalizeText(
                            a?.name
                        ).localeCompare(
                            normalizeText(
                                b?.name
                            )
                        );
                    }
                );
            }, [
                visibleTopics,
            ]);

        // ====================================================
        // CURRICULUM STRUCTURE
        // ====================================================

        const curriculumStructure =
            useMemo(() => {
                const semesterMap =
                    new Map();

                sortedTopics.forEach(
                    (topic) => {
                        const semesterValue =
                            getSemesterValue(
                                topic
                            );

                        const semesterKey =
                            normalizeSemesterKey(
                                semesterValue
                            ) ||
                            "unknown_semester";

                        const semesterName =
                            getSemesterName(
                                topic
                            );

                        if (
                            !semesterMap.has(
                                semesterKey
                            )
                        ) {
                            semesterMap.set(
                                semesterKey,
                                {
                                    key: semesterKey,
                                    value: semesterValue,
                                    name: semesterName,
                                    order:
                                        getSemesterOrder(
                                            semesterValue
                                        ),
                                    periods:
                                        new Map(),
                                }
                            );
                        }

                        const semester =
                            semesterMap.get(
                                semesterKey
                            );

                        const periodNumber =
                            getPeriodNumber(
                                topic
                            );

                        const periodId =
                            getPeriodId(
                                topic
                            );

                        const periodKey =
                            periodNumber !==
                            null
                                ? `number:${periodNumber}`
                                : periodId !==
                                    null &&
                                  periodId !==
                                      undefined
                                ? `id:${periodId}`
                                : `topic:${topic?.id}`;

                        if (
                            !semester.periods.has(
                                periodKey
                            )
                        ) {
                            semester.periods.set(
                                periodKey,
                                {
                                    key: periodKey,
                                    number:
                                        periodNumber,
                                    name:
                                        getPeriodName(
                                            topic
                                        ),
                                    order:
                                        periodNumber ??
                                        999,
                                    topics: [],
                                }
                            );
                        }

                        semester.periods
                            .get(
                                periodKey
                            )
                            .topics.push(
                                topic
                            );
                    }
                );

                return Array.from(
                    semesterMap.values()
                )
                    .sort(
                        (a, b) =>
                            a.order -
                            b.order
                    )
                    .map(
                        (semester) => ({
                            ...semester,
                            periods:
                                Array.from(
                                    semester.periods.values()
                                ).sort(
                                    (a, b) =>
                                        a.order -
                                        b.order
                                ),
                        })
                    );
            }, [
                sortedTopics,
            ]);

        // ====================================================
        // STATISTICS
        // ====================================================

        const statistics =
            useMemo(() => {
                const subjects =
                    new Set();

                const grades =
                    new Set();

                /*
                 * Count actual curriculum periods.
                 *
                 * Example:
                 *
                 * Semester One / Period I
                 * Semester One / Period II
                 * Semester One / Period III
                 * Semester Two / Period IV
                 * Semester Two / Period V
                 * Semester Two / Period VI
                 *
                 * = 6 actual periods.
                 *
                 * Database IDs are deliberately ignored.
                 */

                const periods =
                    new Set();

                const semesters =
                    new Set();

                visibleTopics.forEach(
                    (topic) => {
                        const subjectId =
                            getSubjectId(
                                topic
                            );

                        const gradeId =
                            getGradeId(
                                topic
                            );

                        const semesterValue =
                            getSemesterValue(
                                topic
                            );

                        const semesterKey =
                            normalizeSemesterKey(
                                semesterValue
                            );

                        const periodNumber =
                            getPeriodNumber(
                                topic
                            );

                        if (
                            subjectId !==
                                null &&
                            subjectId !==
                                undefined
                        ) {
                            subjects.add(
                                String(
                                    subjectId
                                )
                            );
                        }

                        if (
                            gradeId !==
                                null &&
                            gradeId !==
                                undefined
                        ) {
                            grades.add(
                                String(
                                    gradeId
                                )
                            );
                        }

                        if (
                            semesterKey
                        ) {
                            semesters.add(
                                semesterKey
                            );
                        }

                        if (
                            periodNumber !==
                                null &&
                            periodNumber !==
                                undefined
                        ) {
                            periods.add(
                                `${
                                    semesterKey ||
                                    "unknown"
                                }:${periodNumber}`
                            );
                        }
                    }
                );

                return {
                    topics:
                        visibleTopics.length,

                    subjects:
                        subjects.size,

                    grades:
                        grades.size,

                    periods:
                        periods.size,

                    semesters:
                        semesters.size,
                };
            }, [
                visibleTopics,
            ]);

        // ====================================================
        // CLEAR FILTERS
        // ====================================================

        const clearFilters =
            useCallback(() => {
                setSearchText("");

                setSelectedSubject(
                    null
                );

                setSelectedGrade(
                    null
                );

                setSelectedSemester(
                    null
                );
            }, []);

        // ====================================================
        // REFRESH
        // ====================================================

        const handleRefresh =
            useCallback(() => {
                loadAvailableSemesters();

                searchCurriculum(
                    searchText
                );
            }, [
                loadAvailableSemesters,
                searchCurriculum,
                searchText,
            ]);

        // ====================================================
        // OPEN TOPIC
        // ====================================================

        const openTopic =
            useCallback(
                async (
                    topic
                ) => {
                    if (
                        !topic?.id
                    ) {
                        messageApi.error(
                            "This curriculum topic does not have a valid ID."
                        );

                        return;
                    }

                    const requestId =
                        ++topicRequestSequenceRef.current;

                    setSelectedTopic(
                        topic
                    );

                    setTopicModalOpen(
                        true
                    );

                    setLoadingTopic(
                        true
                    );

                    try {
                        const response =
                            await api.get(
                                `/api/topics/${topic.id}/`
                            );

                        if (
                            requestId !==
                            topicRequestSequenceRef.current
                        ) {
                            return;
                        }

                        const detailedTopic =
                            response
                                ?.data
                                ?.data ||
                            response
                                ?.data
                                ?.topic ||
                            response?.data;

                        if (
                            detailedTopic &&
                            typeof detailedTopic ===
                                "object"
                        ) {
                            setSelectedTopic(
                                mergeTopicData(
                                    topic,
                                    detailedTopic
                                )
                            );
                        }
                    } catch (
                        requestError
                    ) {
                        if (
                            requestId !==
                            topicRequestSequenceRef.current
                        ) {
                            return;
                        }

                        messageApi.warning(
                            getApiErrorMessage(
                                requestError,
                                "Unable to refresh the detailed topic record. Showing the available curriculum data."
                            )
                        );
                    } finally {
                        if (
                            requestId ===
                            topicRequestSequenceRef.current
                        ) {
                            setLoadingTopic(
                                false
                            );
                        }
                    }
                },
                [messageApi]
            );

        // ====================================================
        // CLOSE TOPIC
        // ====================================================

        const closeTopic =
            useCallback(() => {
                ++topicRequestSequenceRef.current;

                setTopicModalOpen(
                    false
                );

                setLoadingTopic(
                    false
                );

                setSelectedTopic(
                    null
                );
            }, []);

        // ====================================================
        // RENDER TOPIC CARD
        // ====================================================

        const renderTopicCard =
            useCallback(
                (
                    topic,
                    index
                ) => {
                    const subjectName =
                        getSubjectName(
                            topic
                        );

                    const gradeName =
                        getGradeName(
                            topic
                        );

                    const semesterName =
                        getSemesterName(
                            topic
                        );

                    const periodName =
                        getPeriodName(
                            topic
                        );

                    const curriculumName =
                        getCurriculumName(
                            topic
                        );

                    const preview =
                        getPreviewText(
                            topic
                        );

                    const periodNumber =
                        getPeriodNumber(
                            topic
                        );

                    const periodId =
                        getPeriodId(
                            topic
                        );

                    const subjectId =
                        getSubjectId(
                            topic
                        );

                    return (
                        <Col
                            xs={24}
                            sm={24}
                            md={12}
                            xl={8}
                            key={
                                topic?.id ??
                                `topic-${index}`
                            }
                        >
                            <Card
                                hoverable
                                bordered
                                style={{
                                    height:
                                        "100%",
                                    borderRadius:
                                        14,
                                    borderColor:
                                        COLORS.border,
                                    overflow:
                                        "hidden",
                                    transition:
                                        "all 0.2s ease",
                                }}
                                bodyStyle={{
                                    padding: 0,
                                }}
                            >
                                <div
                                    style={{
                                        height: 5,
                                        background:
                                            COLORS.navy,
                                    }}
                                />

                                <div
                                    style={{
                                        padding: 18,
                                    }}
                                >
                                    <Space
                                        size={6}
                                        style={{
                                            marginBottom: 10,
                                        }}
                                    >
                                        <BankOutlined
                                            style={{
                                                color:
                                                    COLORS.khaki,
                                            }}
                                        />

                                        <Text
                                            type="secondary"
                                            style={{
                                                fontSize: 12,
                                            }}
                                        >
                                            {
                                                curriculumName
                                            }
                                        </Text>
                                    </Space>

                                    <Space
                                        wrap
                                        size={[
                                            6,
                                            6,
                                        ]}
                                        style={{
                                            marginBottom: 12,
                                        }}
                                    >
                                        <Tag
                                            icon={
                                                <BookOutlined />
                                            }
                                            color="blue"
                                        >
                                            {
                                                subjectName
                                            }
                                        </Tag>

                                        <Tag
                                            icon={
                                                <TeamOutlined />
                                            }
                                            color="green"
                                        >
                                            {
                                                gradeName
                                            }
                                        </Tag>

                                        <Tag
                                            icon={
                                                <CalendarOutlined />
                                            }
                                        >
                                            {
                                                semesterName
                                            }
                                        </Tag>
                                    </Space>

                                    <Title
                                        level={4}
                                        style={{
                                            marginTop: 0,
                                            marginBottom: 8,
                                            color:
                                                COLORS.navy,
                                            lineHeight:
                                                1.35,
                                        }}
                                    >
                                        {normalizeText(
                                            topic?.name
                                        ) ||
                                            "Untitled Topic"}
                                    </Title>

                                    <Space
                                        size={6}
                                        style={{
                                            marginBottom: 12,
                                        }}
                                    >
                                        <ApartmentOutlined
                                            style={{
                                                color:
                                                    COLORS.orange,
                                            }}
                                        />

                                        <Text
                                            strong
                                            style={{
                                                color:
                                                    COLORS.gray,
                                            }}
                                        >
                                            {
                                                periodName
                                            }
                                        </Text>
                                    </Space>

                                    <Paragraph
                                        ellipsis={{
                                            rows: 4,
                                        }}
                                        style={{
                                            color:
                                                "#475569",
                                            minHeight: 88,
                                            marginBottom: 14,
                                            lineHeight:
                                                1.65,
                                        }}
                                    >
                                        {
                                            preview
                                        }
                                    </Paragraph>

                                    <Space
                                        wrap
                                        size={[
                                            6,
                                            6,
                                        ]}
                                        style={{
                                            marginBottom: 14,
                                        }}
                                    >
                                        {periodNumber !==
                                            null &&
                                            periodNumber !==
                                                undefined && (
                                                <Tag>
                                                    Period #
                                                    {
                                                        periodNumber
                                                    }
                                                </Tag>
                                            )}

                                        {periodId !==
                                            null &&
                                            periodId !==
                                                undefined && (
                                                <Tag>
                                                    Record #
                                                    {
                                                        periodId
                                                    }
                                                </Tag>
                                            )}

                                        {subjectId !==
                                            null &&
                                            subjectId !==
                                                undefined && (
                                                <Tag>
                                                    Subject #
                                                    {
                                                        subjectId
                                                    }
                                                </Tag>
                                            )}
                                    </Space>

                                    <Button
                                        type="primary"
                                        block
                                        icon={
                                            <FolderOpenOutlined />
                                        }
                                        onClick={() =>
                                            openTopic(
                                                topic
                                            )
                                        }
                                        style={{
                                            background:
                                                COLORS.navy,
                                            borderColor:
                                                COLORS.navy,
                                            height: 42,
                                            borderRadius: 9,
                                            fontWeight: 600,
                                        }}
                                    >
                                        Open Topic

                                        <RightOutlined />
                                    </Button>
                                </div>
                            </Card>
                        </Col>
                    );
                },
                [openTopic]
            );

        // ====================================================
        // NO ACADEMIC YEAR
        // ====================================================

        if (
            !selectedYear?.id
        ) {
            return (
                <div
                    style={{
                        padding: 24,
                    }}
                >
                    {contextHolder}

                    <Alert
                        type="warning"
                        showIcon
                        message="No academic year selected"
                        description="Please select an academic year before opening the teacher curriculum workspace."
                    />
                </div>
            );
        }

        // ====================================================
        // MAIN RENDER
        // ====================================================

        return (
            <>
                {contextHolder}

                <div
                    style={{
                        padding:
                            "20px 18px 40px",
                        background:
                            COLORS.light,
                        minHeight:
                            "100vh",
                    }}
                >
                    {/* HEADER */}

                    <Card
                        bordered={false}
                        style={{
                            borderRadius: 16,
                            marginBottom: 18,
                            background:
                                "linear-gradient(135deg, #1B2A41 0%, #263b59 100%)",
                            boxShadow:
                                "0 8px 24px rgba(27,42,65,0.14)",
                        }}
                        bodyStyle={{
                            padding: 24,
                        }}
                    >
                        <Row
                            gutter={[
                                20,
                                20,
                            ]}
                            align="middle"
                        >
                            <Col
                                xs={24}
                                lg={16}
                            >
                                <Space
                                    direction="vertical"
                                    size={7}
                                >
                                    <Tag
                                        icon={
                                            <ReadOutlined />
                                        }
                                        style={{
                                            width:
                                                "fit-content",
                                            background:
                                                "rgba(195,176,145,0.18)",
                                            borderColor:
                                                "rgba(195,176,145,0.35)",
                                            color:
                                                COLORS.khaki,
                                            borderRadius: 20,
                                            padding:
                                                "4px 10px",
                                        }}
                                    >
                                        TEACHER WORKSPACE
                                    </Tag>

                                    <Title
                                        level={2}
                                        style={{
                                            color:
                                                COLORS.white,
                                            margin: 0,
                                        }}
                                    >
                                        Curriculum
                                        Workspace
                                    </Title>

                                    <Text
                                        style={{
                                            color:
                                                "rgba(255,255,255,0.78)",
                                            fontSize: 14,
                                        }}
                                    >
                                        Search and explore
                                        the curriculum
                                        assigned to you for
                                        the selected academic
                                        year.
                                    </Text>
                                </Space>
                            </Col>

                            <Col
                                xs={24}
                                lg={8}
                            >
                                <Card
                                    size="small"
                                    bordered={false}
                                    style={{
                                        borderRadius: 12,
                                        background:
                                            "rgba(255,255,255,0.08)",
                                    }}
                                    bodyStyle={{
                                        padding: 16,
                                    }}
                                >
                                    <Space>
                                        <CalendarOutlined
                                            style={{
                                                color:
                                                    COLORS.khaki,
                                                fontSize: 22,
                                            }}
                                        />

                                        <div>
                                            <Text
                                                style={{
                                                    display:
                                                        "block",
                                                    color:
                                                        "rgba(255,255,255,0.65)",
                                                    fontSize: 12,
                                                }}
                                            >
                                                Academic Year
                                            </Text>

                                            <Text
                                                strong
                                                style={{
                                                    color:
                                                        COLORS.white,
                                                    fontSize: 16,
                                                }}
                                            >
                                                {normalizeText(
                                                    selectedYear?.name
                                                ) ||
                                                    `Year #${selectedYear.id}`}
                                            </Text>
                                        </div>
                                    </Space>
                                </Card>
                            </Col>
                        </Row>
                    </Card>

                    {/* STATISTICS */}

                    <Row
                        gutter={[
                            14,
                            14,
                        ]}
                        style={{
                            marginBottom: 18,
                        }}
                    >
                        <Col
                            xs={12}
                            sm={6}
                        >
                            <Card
                                bordered
                                style={{
                                    borderRadius: 12,
                                    borderColor:
                                        COLORS.border,
                                }}
                            >
                                <Statistic
                                    title="Topics"
                                    value={
                                        statistics.topics
                                    }
                                    prefix={
                                        <ReadOutlined />
                                    }
                                />
                            </Card>
                        </Col>

                        <Col
                            xs={12}
                            sm={6}
                        >
                            <Card
                                bordered
                                style={{
                                    borderRadius: 12,
                                    borderColor:
                                        COLORS.border,
                                }}
                            >
                                <Statistic
                                    title="Subjects"
                                    value={
                                        statistics.subjects
                                    }
                                    prefix={
                                        <BookOutlined />
                                    }
                                />
                            </Card>
                        </Col>

                        <Col
                            xs={12}
                            sm={6}
                        >
                            <Card
                                bordered
                                style={{
                                    borderRadius: 12,
                                    borderColor:
                                        COLORS.border,
                                }}
                            >
                                <Statistic
                                    title="Grades"
                                    value={
                                        statistics.grades
                                    }
                                    prefix={
                                        <TeamOutlined />
                                    }
                                />
                            </Card>
                        </Col>

                        <Col
                            xs={12}
                            sm={6}
                        >
                            <Card
                                bordered
                                style={{
                                    borderRadius: 12,
                                    borderColor:
                                        COLORS.border,
                                }}
                            >
                                <Statistic
                                    title="Periods"
                                    value={
                                        statistics.periods
                                    }
                                    prefix={
                                        <ApartmentOutlined />
                                    }
                                />
                            </Card>
                        </Col>
                    </Row>

                    {/* SEARCH / FILTER */}

                    <Card
                        bordered
                        style={{
                            borderRadius: 14,
                            marginBottom: 18,
                            borderColor:
                                COLORS.border,
                        }}
                        bodyStyle={{
                            padding: 18,
                        }}
                    >
                        <Space
                            align="center"
                            style={{
                                marginBottom: 14,
                            }}
                        >
                            <FilterOutlined
                                style={{
                                    color:
                                        COLORS.navy,
                                    fontSize: 18,
                                }}
                            />

                            <Text
                                strong
                                style={{
                                    color:
                                        COLORS.navy,
                                    fontSize: 16,
                                }}
                            >
                                Search & Filter
                            </Text>
                        </Space>

                        <Row
                            gutter={[
                                12,
                                12,
                            ]}
                        >
                            <Col
                                xs={24}
                                lg={9}
                            >
                                <Input
                                    allowClear
                                    size="large"
                                    prefix={
                                        <SearchOutlined />
                                    }
                                    placeholder="Search topics, content, competencies, resources..."
                                    value={
                                        searchText
                                    }
                                    onChange={(
                                        event
                                    ) =>
                                        setSearchText(
                                            event
                                                .target
                                                .value
                                        )
                                    }
                                />
                            </Col>

                            <Col
                                xs={24}
                                sm={12}
                                lg={4}
                            >
                                <Select
                                    allowClear
                                    showSearch
                                    size="large"
                                    style={{
                                        width: "100%",
                                    }}
                                    placeholder="Subject"
                                    value={
                                        selectedSubject ??
                                        undefined
                                    }
                                    onChange={
                                        setSelectedSubject
                                    }
                                    optionFilterProp="label"
                                >
                                    {subjectOptions.map(
                                        (
                                            subject
                                        ) => (
                                            <Option
                                                key={
                                                    subject.id
                                                }
                                                value={
                                                    subject.id
                                                }
                                                label={
                                                    subject.name
                                                }
                                            >
                                                {
                                                    subject.name
                                                }
                                            </Option>
                                        )
                                    )}
                                </Select>
                            </Col>

                            <Col
                                xs={24}
                                sm={12}
                                lg={4}
                            >
                                <Select
                                    allowClear
                                    showSearch
                                    size="large"
                                    style={{
                                        width: "100%",
                                    }}
                                    placeholder="Grade / Class"
                                    value={
                                        selectedGrade ??
                                        undefined
                                    }
                                    onChange={
                                        setSelectedGrade
                                    }
                                    optionFilterProp="label"
                                >
                                    {gradeOptions.map(
                                        (
                                            grade
                                        ) => (
                                            <Option
                                                key={
                                                    grade.id
                                                }
                                                value={
                                                    grade.id
                                                }
                                                label={
                                                    grade.name
                                                }
                                            >
                                                {
                                                    grade.name
                                                }
                                            </Option>
                                        )
                                    )}
                                </Select>
                            </Col>

                            <Col
                                xs={24}
                                sm={12}
                                lg={4}
                            >
                                <Select
                                    allowClear
                                    showSearch
                                    size="large"
                                    style={{
                                        width: "100%",
                                    }}
                                    placeholder="Semester"
                                    value={
                                        selectedSemester ??
                                        undefined
                                    }
                                    onChange={
                                        setSelectedSemester
                                    }
                                    optionFilterProp="label"
                                    loading={
                                        semestersLoading
                                    }
                                    notFoundContent={
                                        semestersLoading ? (
                                            <Space>
                                                <Spin
                                                    size="small"
                                                />
                                                <span>
                                                    Loading semesters...
                                                </span>
                                            </Space>
                                        ) : (
                                            "No semesters available"
                                        )
                                    }
                                >
                                    {semesterOptions.map(
                                        (
                                            semester
                                        ) => (
                                            <Option
                                                key={String(
                                                    semester.value
                                                )}
                                                value={
                                                    semester.value
                                                }
                                                label={
                                                    semester.name
                                                }
                                            >
                                                {
                                                    semester.name
                                                }
                                            </Option>
                                        )
                                    )}
                                </Select>
                            </Col>

                            <Col
                                xs={24}
                                sm={12}
                                lg={3}
                            >
                                <Button
                                    size="large"
                                    block
                                    icon={
                                        <ClearOutlined />
                                    }
                                    onClick={
                                        clearFilters
                                    }
                                >
                                    Clear
                                </Button>
                            </Col>
                        </Row>

                        <div
                            style={{
                                display:
                                    "flex",
                                justifyContent:
                                    "space-between",
                                alignItems:
                                    "center",
                                flexWrap:
                                    "wrap",
                                gap: 10,
                                marginTop: 14,
                            }}
                        >
                            <Text type="secondary">
                                Showing{" "}
                                <Text strong>
                                    {
                                        visibleTopics.length
                                    }
                                </Text>{" "}
                                curriculum topic
                                {visibleTopics.length !==
                                1
                                    ? "s"
                                    : ""}
                            </Text>

                            <Button
                                icon={
                                    <ReloadOutlined />
                                }
                                onClick={
                                    handleRefresh
                                }
                                loading={
                                    loading ||
                                    semestersLoading
                                }
                            >
                                Refresh
                            </Button>
                        </div>
                    </Card>

                    {/* ERROR */}

                    {error && (
                        <Alert
                            type="error"
                            showIcon
                            closable
                            message="Unable to load curriculum"
                            description={
                                <>
                                    {error}

                                    <div
                                        style={{
                                            marginTop: 10,
                                        }}
                                    >
                                        <Button
                                            size="small"
                                            icon={
                                                <ReloadOutlined />
                                            }
                                            onClick={
                                                handleRefresh
                                            }
                                        >
                                            Try Again
                                        </Button>
                                    </div>
                                </>
                            }
                            style={{
                                marginBottom: 18,
                                borderRadius: 10,
                            }}
                        />
                    )}

                    {/* INITIAL LOADING */}

                    {initialLoading ? (
                        <Card
                            bordered
                            style={{
                                minHeight: 400,
                                borderRadius: 14,
                                display:
                                    "flex",
                                justifyContent:
                                    "center",
                                alignItems:
                                    "center",
                            }}
                        >
                            <Space
                                direction="vertical"
                                align="center"
                            >
                                <Spin size="large" />

                                <Text type="secondary">
                                    Loading your
                                    curriculum...
                                </Text>
                            </Space>
                        </Card>
                    ) : visibleTopics.length ===
                      0 ? (
                        <Card
                            bordered
                            style={{
                                borderRadius: 14,
                                borderColor:
                                    COLORS.border,
                            }}
                        >
                            <Empty
                                image={
                                    Empty.PRESENTED_IMAGE_SIMPLE
                                }
                                description={
                                    searchText ||
                                    selectedSubject ||
                                    selectedGrade ||
                                    selectedSemester
                                        ? "No curriculum topics match the current search or filters."
                                        : "No curriculum topics are available for this academic year."
                                }
                            >
                                {(searchText ||
                                    selectedSubject ||
                                    selectedGrade ||
                                    selectedSemester) && (
                                    <Button
                                        icon={
                                            <ClearOutlined />
                                        }
                                        onClick={
                                            clearFilters
                                        }
                                    >
                                        Clear Filters
                                    </Button>
                                )}
                            </Empty>
                        </Card>
                    ) : (
                        <>
                            {loading && (
                                <div
                                    style={{
                                        marginBottom: 12,
                                    }}
                                >
                                    <Alert
                                        type="info"
                                        showIcon
                                        message="Updating curriculum results..."
                                    />
                                </div>
                            )}

                            {/* ==================================================
                                CURRICULUM HIERARCHY
                            ================================================== */}

                            {curriculumStructure.map(
                                (
                                    semester
                                ) => (
                                    <Card
                                        key={
                                            semester.key
                                        }
                                        bordered
                                        style={{
                                            marginBottom: 20,
                                            borderRadius: 16,
                                            borderColor:
                                                COLORS.border,
                                            overflow:
                                                "hidden",
                                        }}
                                        bodyStyle={{
                                            padding: 0,
                                        }}
                                    >
                                        {/* SEMESTER HEADER */}

                                        <div
                                            style={{
                                                background:
                                                    COLORS.navy,
                                                padding:
                                                    "18px 20px",
                                                display:
                                                    "flex",
                                                justifyContent:
                                                    "space-between",
                                                alignItems:
                                                    "center",
                                                flexWrap:
                                                    "wrap",
                                                gap: 10,
                                            }}
                                        >
                                            <Space
                                                size={10}
                                            >
                                                <CalendarOutlined
                                                    style={{
                                                        color:
                                                            COLORS.khaki,
                                                        fontSize: 22,
                                                    }}
                                                />

                                                <div>
                                                    <Text
                                                        style={{
                                                            display:
                                                                "block",
                                                            color:
                                                                "rgba(255,255,255,0.65)",
                                                            fontSize: 12,
                                                        }}
                                                    >
                                                        Curriculum
                                                        Semester
                                                    </Text>

                                                    <Title
                                                        level={3}
                                                        style={{
                                                            color:
                                                                COLORS.white,
                                                            margin: 0,
                                                        }}
                                                    >
                                                        {
                                                            semester.name
                                                        }
                                                    </Title>
                                                </div>
                                            </Space>

                                            <Space
                                                wrap
                                            >
                                                <Tag
                                                    style={{
                                                        margin: 0,
                                                        borderRadius: 20,
                                                        padding:
                                                            "3px 10px",
                                                    }}
                                                >
                                                    {
                                                        semester.periods.length
                                                    }{" "}
                                                    period
                                                    {semester
                                                        .periods
                                                        .length !==
                                                    1
                                                        ? "s"
                                                        : ""}
                                                </Tag>

                                                <Tag
                                                    color="blue"
                                                    style={{
                                                        margin: 0,
                                                        borderRadius: 20,
                                                        padding:
                                                            "3px 10px",
                                                    }}
                                                >
                                                    {semester.periods.reduce(
                                                        (
                                                            total,
                                                            period
                                                        ) =>
                                                            total +
                                                            period
                                                                .topics
                                                                .length,
                                                        0
                                                    )}{" "}
                                                    topic
                                                    {semester.periods.reduce(
                                                        (
                                                            total,
                                                            period
                                                        ) =>
                                                            total +
                                                            period
                                                                .topics
                                                                .length,
                                                        0
                                                    ) !==
                                                    1
                                                        ? "s"
                                                        : ""}
                                                </Tag>
                                            </Space>
                                        </div>

                                        <div
                                            style={{
                                                padding:
                                                    "18px",
                                            }}
                                        >
                                            {semester.periods.map(
                                                (
                                                    period
                                                ) => (
                                                    <div
                                                        key={
                                                            period.key
                                                        }
                                                        style={{
                                                            marginBottom:
                                                                22,
                                                        }}
                                                    >
                                                        {/* PERIOD HEADER */}

                                                        <div
                                                            style={{
                                                                display:
                                                                    "flex",
                                                                justifyContent:
                                                                    "space-between",
                                                                alignItems:
                                                                    "center",
                                                                flexWrap:
                                                                    "wrap",
                                                                gap: 10,
                                                                padding:
                                                                    "12px 14px",
                                                                marginBottom:
                                                                    14,
                                                                background:
                                                                    "#f8fafc",
                                                                border:
                                                                    `1px solid ${COLORS.border}`,
                                                                borderRadius:
                                                                    10,
                                                            }}
                                                        >
                                                            <Space
                                                                size={
                                                                    8
                                                                }
                                                            >
                                                                <ApartmentOutlined
                                                                    style={{
                                                                        color:
                                                                            COLORS.orange,
                                                                        fontSize: 18,
                                                                    }}
                                                                />

                                                                <Text
                                                                    strong
                                                                    style={{
                                                                        color:
                                                                            COLORS.navy,
                                                                        fontSize: 16,
                                                                    }}
                                                                >
                                                                    {
                                                                        period.name
                                                                    }
                                                                </Text>
                                                            </Space>

                                                            <Tag
                                                                style={{
                                                                    margin: 0,
                                                                }}
                                                            >
                                                                {
                                                                    period
                                                                        .topics
                                                                        .length
                                                                }{" "}
                                                                topic
                                                                {period
                                                                    .topics
                                                                    .length !==
                                                                1
                                                                    ? "s"
                                                                    : ""}
                                                            </Tag>
                                                        </div>

                                                        {/* TOPICS */}

                                                        <Row
                                                            gutter={[
                                                                16,
                                                                16,
                                                            ]}
                                                        >
                                                            {period.topics.map(
                                                                (
                                                                    topic,
                                                                    index
                                                                ) =>
                                                                    renderTopicCard(
                                                                        topic,
                                                                        index
                                                                    )
                                                            )}
                                                        </Row>
                                                    </div>
                                                )
                                            )}
                                        </div>
                                    </Card>
                                )
                            )}
                        </>
                    )}

                    {/* TOPIC POPUP */}

                    <CurriculumTeacherWorkspacePopup
                        open={
                            topicModalOpen
                        }
                        topic={
                            selectedTopic
                        }
                        loading={
                            loadingTopic
                        }
                        onClose={
                            closeTopic
                        }
                        selectedYear={
                            selectedYear
                        }
                    />
                </div>
            </>
        );
    };

export default CurriculumTeacherWorkspace;